import type { ComoRepartir, DailyRevenue, ExpenseCategory, ExpenseEntry, NoEsGasto, RepartoGasto } from './types';
import { isMaterialCategory, revenueOf, visibleCategory } from './finance';
import { addMonths, monthDiff, monthOf } from './period';
import { round } from './units';

/**
 * PAGOS QUE CUBREN VARIOS MESES (decidido con Alex el 11 oct 2026).
 *
 * Antes existía la pestaña «Impuestos», que creaba un gasto suelto por mes
 * (día 28) sin nada que los uniera. Ahora el reparto vive DENTRO del gasto:
 *
 *  - En Gastos (la lista) el pago sale entero el día que se pagó, con la
 *    etiqueta «Repartido en 12 meses · $750 al mes» (`etiquetaReparto`).
 *  - En el Estado de resultados y en Análisis cada mes carga solo su parte
 *    (`prorratearGastos`, que se aplica al cargar los datos del resultado).
 *  - La pregunta «¿Este pago cubre varios meses?» sale SOLO cuando toca
 *    (`debePreguntarReparto`), con la respuesta ya propuesta
 *    (`proponerReparto`) y recordando lo que se dijo la vez anterior del
 *    mismo proveedor y categoría (`repartoRecordado`).
 */

export const MAX_MESES_REPARTO = 24;

const MESES_LARGOS = ['enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio', 'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre'];
const MESES_CORTOS = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];

/** «2026-09» → «septiembre». */
export const mesEnPalabras = (month: string): string => MESES_LARGOS[Number(month.slice(5, 7)) - 1] ?? month;
/** «2026-09» → «sep». */
export const nombreMesCorto = (month: string): string => MESES_CORTOS[Number(month.slice(5, 7)) - 1] ?? month;
/** «2026-09» → «sep 2026». */
const mesYAnio = (month: string): string => `${nombreMesCorto(month)} ${month.slice(0, 4)}`;

/** «julio, agosto y septiembre». */
export function listaDeMeses(months: string[], corto = false): string {
  const nombres = months.map(corto ? nombreMesCorto : mesEnPalabras);
  if (nombres.length <= 1) return nombres.join('');
  return `${nombres.slice(0, -1).join(', ')} y ${nombres[nombres.length - 1]}`;
}

/** Los meses que cubre un reparto, en orden. */
export function mesesDelReparto(reparto: Pick<RepartoGasto, 'meses' | 'desde'>): string[] {
  const n = Math.max(1, Math.min(MAX_MESES_REPARTO, Math.round(reparto.meses || 1)));
  return Array.from({ length: n }, (_, i) => addMonths(reparto.desde, i));
}

/** ¿Este reparto mueve algo? (`meses: 1` en el mes del pago = «No, es solo de este mes»). */
export function repartoActivo(expense: Pick<ExpenseEntry, 'date' | 'reparto'>): boolean {
  const r = expense.reparto;
  if (!r || !/^\d{4}-\d{2}$/.test(r.desde)) return false;
  return r.meses > 1 || r.desde !== monthOf(expense.date);
}

/** Bases de los repartos «según ventas» y «según sueldos», por mes. */
export interface BasesDeReparto {
  ventas: Map<string, number>;
  sueldos: Map<string, number>;
}

/**
 * Ventas y sueldos de cada mes. Los sueldos salen de Personal › Sueldos, sin
 * los pagos que se reparten (el aguinaldo se reparte SEGÚN los sueldos: no
 * puede formar parte de su propia base).
 */
export function basesDeReparto(revenues: DailyRevenue[], expenses: ExpenseEntry[]): BasesDeReparto {
  const ventas = new Map<string, number>();
  for (const day of revenues) {
    const m = monthOf(day.date);
    ventas.set(m, (ventas.get(m) ?? 0) + revenueOf(day));
  }
  const sueldos = new Map<string, number>();
  for (const e of expenses) {
    if (visibleCategory(e.category) !== 'sueldos_salarios' || repartoActivo(e) || e.noEsGasto) continue;
    const m = monthOf(e.date);
    sueldos.set(m, (sueldos.get(m) ?? 0) + e.netAmount);
  }
  return { ventas, sueldos };
}

/**
 * Los meses que hacen falta para las bases de los repartos «según ventas» o
 * «según sueldos» de estos gastos (primero y último), o nada si no hay.
 */
export function mesesDeLasBases(expenses: ExpenseEntry[]): { desde: string; hasta: string } | undefined {
  let desde: string | undefined;
  let hasta: string | undefined;
  for (const e of expenses) {
    if (!repartoActivo(e) || e.reparto!.como === 'iguales') continue;
    const meses = mesesDelReparto(e.reparto!);
    if (!desde || meses[0] < desde) desde = meses[0];
    if (!hasta || meses[meses.length - 1] > hasta) hasta = meses[meses.length - 1];
  }
  return desde && hasta ? { desde, hasta } : undefined;
}

/**
 * La parte de cada mes (regla 3):
 *  - Partes iguales: total ÷ meses.
 *  - Según ventas: total × ventas del mes ÷ ventas de los meses que cubre.
 *  - Según sueldos: igual, con Personal › Sueldos de cada mes.
 *  - Si algún mes aún no tiene base (pago por adelantado), partes iguales
 *    hasta que la tenga: se recalcula solo cada vez que se mira.
 * Los céntimos que sobran del redondeo van al último mes: la suma CUADRA.
 */
export function partesDelReparto(
  total: number,
  reparto: RepartoGasto,
  bases?: BasesDeReparto,
): Array<{ mes: string; importe: number }> {
  const meses = mesesDelReparto(reparto);
  let pesos: number[];
  if (reparto.partes && reparto.partes.length === meses.length && Math.abs(reparto.partes.reduce((a, b) => a + b, 0) - total) <= 0.05) {
    pesos = reparto.partes;
  } else if (reparto.como !== 'iguales' && bases) {
    const mapa = reparto.como === 'ventas' ? bases.ventas : bases.sueldos;
    const valores = meses.map((m) => Math.max(0, mapa.get(m) ?? 0));
    pesos = meses.every((m) => mapa.has(m)) && valores.some((v) => v > 0) ? valores : meses.map(() => 1);
  } else {
    pesos = meses.map(() => 1);
  }
  const suma = pesos.reduce((a, b) => a + b, 0) || 1;
  const partes = meses.map((mes, i) => ({ mes, importe: round((total * pesos[i]) / suma, 2) }));
  const residuo = round(total - partes.reduce((a, p) => a + p.importe, 0), 2);
  if (residuo !== 0 && partes.length > 0) partes[partes.length - 1].importe = round(partes[partes.length - 1].importe + residuo, 2);
  return partes;
}

/**
 * Lo que entra en el resultado: cada pago repartido se convierte en una parte
 * por mes (con `parteDe`), el resto queda igual. Se aplica al CARGAR los datos
 * del resultado (API: `loadFinance`; demo: `financeInput`), antes de repartir
 * los gastos comunes entre locales.
 *
 * Cada parte conserva la fecha de pago del original (`paymentDate`): el flujo
 * de caja sigue viendo salir el pago entero el día que salió.
 */
export function prorratearGastos(
  expenses: ExpenseEntry[],
  revenues: DailyRevenue[],
  /**
   * Bases ya calculadas con TODOS los meses que cubren los repartos (la API
   * las pide aparte): la parte de un mes no puede depender del periodo que
   * se mira. Ausentes, salen de `revenues` y `expenses`.
   */
  basesYa?: BasesDeReparto,
): ExpenseEntry[] {
  if (!expenses.some(repartoActivo)) return expenses;
  const bases = basesYa ?? basesDeReparto(revenues, expenses);
  const out: ExpenseEntry[] = [];
  for (const e of expenses) {
    if (!repartoActivo(e)) { out.push(e); continue; }
    const mesPago = monthOf(e.date);
    const partes = partesDelReparto(e.netAmount, e.reparto!, bases);
    const pagado = e.paymentDate ?? (e.paid ? e.date : undefined);
    partes.forEach((p, i) => {
      out.push({
        ...e,
        date: p.mes === mesPago ? e.date : `${p.mes}-01`,
        netAmount: p.importe,
        ...(pagado ? { paymentDate: pagado } : {}),
        parteDe: { entryId: e.entryId, n: i + 1, de: partes.length, total: e.netAmount },
      });
    });
  }
  return out;
}

/**
 * Un reparto de la pestaña «Impuestos» vieja: un gasto suelto por mes con el
 * concepto «<concepto> (pago de <mes>, repartido)». Decidido con Alex el 14
 * oct 2026: NO se reagrupan (si sale mal, cambian meses ya cerrados); se
 * quedan como están, con la etiqueta gris «Reparto antiguo».
 *
 * Aquella pestaña NUNCA guardaba el pago entero: solo creaba las piezas (el
 * cálculo de `/v1/analytics/tax-allocation` no escribía nada). Solo cuenta
 * dos veces si el dueño ya lo había subido como un gasto aparte, y la
 * pantalla le pedía borrarlo; eso no se puede distinguir sin ambigüedad de
 * otro gasto del mismo importe, así que no se quita nada del resultado.
 */
export function esRepartoAntiguo(expense: Pick<ExpenseEntry, 'concept'>): boolean {
  return /\(pago de [^,()]+, repartido\)\s*$/.test(expense.concept ?? '');
}

/**
 * La etiqueta de la lista de Gastos: «Repartido en 12 meses · $750 al mes»
 * (partes iguales) o «Repartido en 3 meses · según ventas». Sin reparto que
 * mueva algo, nada.
 */
export function etiquetaReparto(expense: Pick<ExpenseEntry, 'date' | 'netAmount' | 'reparto'>, formatMoney: (n: number) => string): string | undefined {
  if (!repartoActivo(expense)) return undefined;
  const r = expense.reparto!;
  if (r.meses <= 1) return `Cuenta en ${mesEnPalabras(r.desde)}`;
  const base = `Repartido en ${r.meses} meses`;
  if (r.partes || r.como !== 'iguales') return `${base} · según ${r.como === 'sueldos' ? 'sueldos' : r.como === 'ventas' ? 'ventas' : 'lo que pusiste'}`;
  return `${base} · ${formatMoney(round(expense.netAmount / r.meses, 2))} al mes`;
}

/* ------------------------------------------------------------------ */
/* Regla 1: cuándo se pregunta                                         */
/* ------------------------------------------------------------------ */

const sinAcentos = (t: string) => t.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

/** Palabras que dicen que un pago cubre más de un mes. */
const PALABRAS_VARIOS_MESES = /\b(anual|anuales|ano|trimestre|trimestral|t[1-4]|[1-4](er|ro|do|to|o)?\.? trimestre|bimestre|bimestral|semestre|semestral|aguinaldo|predial|prima|ibi|licencia anual)\b/;
const PALABRAS_PERIODO_LARGO = /\b(anual|anuales|trimestre|trimestral|t[1-4]|semestre|semestral|bimestre|bimestral)\b/;

/** Categorías en las que se pregunta siempre (seguros, impuestos, seguridad social, software). */
const CATEGORIAS_QUE_PREGUNTAN: ExpenseCategory[] = ['seguros', 'impuesto_ganancias', 'cuotas_ss', 'comunicacion_web'];

export interface GastoParaRepartir {
  concept: string;
  category: ExpenseCategory;
  netAmount: number;
  /** Fecha del pago o de la factura, YYYY-MM-DD. */
  date: string;
  supplierId?: string;
  entryId?: string;
}

/** Lo normal de un proveedor: la mediana de sus gastos anteriores (al menos 2). */
function normalDelProveedor(historial: ExpenseEntry[], gasto: GastoParaRepartir): number | undefined {
  if (!gasto.supplierId) return undefined;
  const importes = historial
    .filter((e) => e.supplierId === gasto.supplierId && e.entryId !== gasto.entryId && !e.parteDe)
    .map((e) => e.netAmount)
    .filter((n) => n > 0)
    .sort((a, b) => a - b);
  if (importes.length < 2) return undefined;
  const mitad = Math.floor(importes.length / 2);
  return importes.length % 2 ? importes[mitad] : (importes[mitad - 1] + importes[mitad]) / 2;
}

/**
 * ¿Sale la pregunta «¿Este pago cubre varios meses?»? (regla 1). En el 95 %
 * de los gastos, no:
 *  - Nunca para Materia prima ni para la renta mensual.
 *  - Sí en Seguros, Impuestos, Seguridad social y Software; Contador solo si
 *    el concepto dice trimestral/anual.
 *  - Sí si el concepto dice «anual», «trimestre», «T3», «bimestre»,
 *    «aguinaldo», «predial», «prima»…
 *  - Sí si el monto pasa de 3 veces lo normal de ese proveedor.
 */
export function debePreguntarReparto(gasto: GastoParaRepartir, historial: ExpenseEntry[] = []): boolean {
  if (!(gasto.netAmount > 0)) return false;
  const categoria = visibleCategory(gasto.category);
  if (isMaterialCategory(categoria)) return false;
  const texto = sinAcentos(gasto.concept ?? '');
  const diceVariosMeses = PALABRAS_VARIOS_MESES.test(texto);
  if (categoria === 'alquiler') return PALABRAS_PERIODO_LARGO.test(texto);
  if (categoria === 'honorarios') return PALABRAS_PERIODO_LARGO.test(texto) || diceVariosMeses;
  if (CATEGORIAS_QUE_PREGUNTAN.includes(categoria)) return true;
  if (diceVariosMeses) return true;
  const normal = normalDelProveedor(historial, gasto);
  return normal != null && gasto.netAmount > normal * 3;
}

/* ------------------------------------------------------------------ */
/* Regla 2: propuesta automática                                       */
/* ------------------------------------------------------------------ */

export interface PropuestaReparto {
  reparto: RepartoGasto;
  /** Por qué: «Parece el seguro anual del local…». */
  explicacion: string;
  /** «Sí, repartir en 12 meses». */
  botonSi: string;
  /** «No, es solo de octubre». */
  botonNo: string;
  /** Qué se proponía (para tests y para el texto). */
  tipo: 'anual' | 'semestre' | 'trimestre' | 'bimestre' | 'aguinaldo' | 'otro';
}

/** El trimestre que dice el concepto: «T3», «3er trimestre», «tercer trimestre». */
function trimestreDelConcepto(texto: string): number | undefined {
  const t = texto.match(/\bt([1-4])\b/) ?? texto.match(/\b([1-4])\s*(er|ro|do|to|o)?\.?\s*trimestre\b/);
  if (t) return Number(t[1]);
  const palabras: Array<[RegExp, number]> = [[/\bprimer\b/, 1], [/\bsegundo\b/, 2], [/\btercer\b/, 3], [/\bcuarto\b/, 4]];
  if (/trimestre/.test(texto)) for (const [re, n] of palabras) if (re.test(texto)) return n;
  return undefined;
}

const ordinalTrimestre = (q: number) => ['1er', '2do', '3er', '4to'][q - 1];

/** Primer mes de un trimestre en el año del pago (o del anterior si aún no ha empezado). */
function inicioTrimestre(q: number, mesPago: string): string {
  let anio = Number(mesPago.slice(0, 4));
  const inicio = (y: number) => `${y}-${String((q - 1) * 3 + 1).padStart(2, '0')}`;
  if (inicio(anio) > mesPago) anio -= 1;
  return inicio(anio);
}

/**
 * La propuesta que la persona solo confirma (regla 2):
 *  - Seguro, licencia, software anual, predial → 12 meses desde el mes de la
 *    factura, partes iguales.
 *  - Impuesto trimestral → los 3 meses del trimestre que diga (T3 = jul, ago,
 *    sep), según ventas. Sin número, el trimestre anterior al del pago.
 *  - Seguridad social bimestral → 2 meses desde el mes del pago,
 *    según sueldos.
 *  - Aguinaldo → los 12 meses del año, según sueldos.
 *  - Si no se sabe: 12 meses, partes iguales.
 */
export function proponerReparto(gasto: GastoParaRepartir, hoy?: string): PropuestaReparto {
  const mesPago = monthOf(gasto.date);
  const categoria = visibleCategory(gasto.category);
  const texto = sinAcentos(gasto.concept ?? '');
  const decididoEl = hoy;
  const conFecha = (r: RepartoGasto): RepartoGasto => (decididoEl ? { ...r, decididoEl } : r);
  const noBoton = `No, es solo de ${mesEnPalabras(mesPago)}`;
  const como: ComoRepartir = categoria === 'impuesto_ganancias' ? 'ventas'
    : categoria === 'cuotas_ss' || categoria === 'sueldos_salarios' || /aguinaldo/.test(texto) ? 'sueldos' : 'iguales';
  const trimestreAnterior = (((Math.ceil(Number(mesPago.slice(5, 7)) / 3) + 2) % 4) + 1);
  const desdePorDefecto = categoria === 'impuesto_ganancias' ? inicioTrimestre(trimestreAnterior, mesPago) : mesPago;

  if (/\baguinaldo\b/.test(texto)) {
    // Se paga en diciembre y se genera todo el año. Pagado en enero o
    // febrero, es el del año anterior.
    const mes = Number(mesPago.slice(5, 7));
    const anio = Number(mesPago.slice(0, 4)) - (mes <= 2 ? 1 : 0);
    const desde = `${anio}-01`;
    return {
      tipo: 'aguinaldo',
      reparto: conFecha({ meses: 12, desde, como: 'sueldos' }),
      explicacion: `Parece el aguinaldo de ${anio}: se paga en diciembre, pero se genera todo el año. Lo reparto en los 12 meses según los sueldos de cada uno.`,
      botonSi: 'Sí, repartir en 12 meses',
      botonNo: noBoton,
    };
  }

  const q = trimestreDelConcepto(texto);
  if (q || /\btrimestr/.test(texto) || (categoria === 'impuesto_ganancias' && !/semestr|bimestr|anual/.test(texto))) {
    const trimestre = q ?? trimestreAnterior;
    const desde = q ? inicioTrimestre(q, mesPago) : desdePorDefecto;
    const meses = [desde, addMonths(desde, 1), addMonths(desde, 2)];
    return {
      tipo: 'trimestre',
      reparto: conFecha({ meses: 3, desde, como }),
      explicacion: `Cubre ${listaDeMeses(meses)}${categoria === 'impuesto_ganancias' ? `, del ${ordinalTrimestre(trimestre)} trimestre` : ''}. ${como === 'iguales' ? 'Se reparte en partes iguales.' : `Se reparte según ${como === 'ventas' ? 'las ventas' : 'los sueldos'} de cada mes.`}`,
      botonSi: 'Sí, repartir en 3 meses',
      botonNo: `No, que cuente en ${mesEnPalabras(mesPago)}`,
    };
  }

  if (/\bsemestr/.test(texto)) {
    return {
      tipo: 'semestre', reparto: conFecha({ meses: 6, desde: desdePorDefecto, como }),
      explicacion: 'Parece un pago semestral: cubre 6 meses.',
      botonSi: 'Sí, repartir en 6 meses', botonNo: noBoton,
    };
  }
  if (/\bbimestr/.test(texto) || (categoria === 'cuotas_ss' && !/anual/.test(texto))) {
    const desde = desdePorDefecto;
    const meses = [desde, addMonths(desde, 1)];
    return {
      tipo: 'bimestre',
      reparto: conFecha({ meses: 2, desde, como }),
      explicacion: `Parece un pago bimestral: cubre ${listaDeMeses(meses)}.`,
      botonSi: 'Sí, repartir en 2 meses',
      botonNo: noBoton,
    };
  }

  const queEs =
    categoria === 'seguros' ? 'el seguro anual del local'
      : /predial|\bibi\b/.test(texto) ? 'el predial del año'
        : categoria === 'comunicacion_web' || /licencia/.test(texto) ? 'una licencia o suscripción anual'
          : 'un pago anual';
  return {
    tipo: queEs === 'un pago anual' ? 'otro' : 'anual',
    reparto: conFecha({ meses: 12, desde: desdePorDefecto, como }),
    explicacion: `Parece ${queEs}: cubre 12 meses.`,
    botonSi: 'Sí, repartir en 12 meses',
    botonNo: noBoton,
  };
}

/** «No, es solo de octubre»: no reparte, pero queda dicho y se recuerda. */
export function repartoSoloDeEsteMes(date: string, hoy?: string): RepartoGasto {
  return { meses: 1, desde: monthOf(date), como: 'iguales', ...(hoy ? { decididoEl: hoy } : {}) };
}

/* ------------------------------------------------------------------ */
/* Regla 6: recordar por proveedor + categoría                         */
/* ------------------------------------------------------------------ */

/**
 * Lo que se decidió la última vez para el mismo proveedor y categoría (sin
 * proveedor, la misma categoría; si no hay coincidencia, se usa la categoría). Se propone igual,
 * movido al mes del pago nuevo: el T3 pagado en octubre cubre jul–sep; el T4
 * pagado en enero, oct–dic.
 */
export function repartoRecordado(historial: ExpenseEntry[], gasto: GastoParaRepartir): RepartoGasto | undefined {
  const categoria = visibleCategory(gasto.category);
  const candidatos = historial
    .filter((e) =>
      e.reparto && !e.parteDe && e.entryId !== gasto.entryId &&
      visibleCategory(e.category) === categoria &&
      e.date <= gasto.date,
    );
  const delProveedor = candidatos.filter((e) => (e.supplierId ?? '') === (gasto.supplierId ?? ''));
  const anterior = (delProveedor.length ? delProveedor : candidatos)
    .sort((a, b) => (b.reparto?.decididoEn ?? b.reparto?.decididoEl ?? b.date).localeCompare(a.reparto?.decididoEn ?? a.reparto?.decididoEl ?? a.date))[0];
  if (!anterior?.reparto) return undefined;
  const desplazamiento = monthDiff(monthOf(anterior.date), anterior.reparto.desde);
  const { partes: _partes, decididoEl: _d, decididoEn: _en, recordado: _r, ...pauta } = anterior.reparto;
  return { ...pauta, desde: addMonths(monthOf(gasto.date), desplazamiento), recordado: true };
}

/** «La repartí en 12 meses, como la anterior.» / «Cuenta entero en octubre, como la anterior.» */
export function textoRecordado(reparto: RepartoGasto, date: string): string {
  if (reparto.meses <= 1 && reparto.desde === monthOf(date)) return `Cuenta entero en ${mesEnPalabras(reparto.desde)}, como la anterior.`;
  return `La repartí en ${reparto.meses} meses${reparto.como === 'iguales' ? '' : reparto.como === 'ventas' ? ' según ventas' : ' según sueldos'}, como la anterior.`;
}

/* ------------------------------------------------------------------ */
/* Regla 5: meses ya cerrados                                          */
/* ------------------------------------------------------------------ */

/** Meses del reparto anteriores al mes en curso (ya cerrados). */
export function mesesCerradosDelReparto(reparto: RepartoGasto, hoy: string): string[] {
  const actual = monthOf(hoy);
  return mesesDelReparto(reparto).filter((m) => m < actual);
}

/** «Esto cambia julio, agosto y septiembre, que ya estaban cerrados…». */
export function avisoMesesCerrados(reparto: RepartoGasto, hoy: string): string | undefined {
  const cerrados = mesesCerradosDelReparto(reparto, hoy);
  if (cerrados.length === 0) return undefined;
  const cuales = listaDeMeses(cerrados);
  return cerrados.length === 1
    ? `Esto cambia ${cuales}, que ya estaba cerrado. Te lo marco en su Estado de resultados para que no te sorprenda.`
    : `Esto cambia ${cuales}, que ya estaban cerrados. Te lo marco en su Estado de resultados para que no te sorprenda.`;
}

/** «Repartido de oct 2026 a sep 2027: $750 al mes» / «Repartido según ventas: jul $1,155 · ago $1,334 · sep $1,231». */
export function resumenReparto(
  total: number,
  reparto: RepartoGasto,
  formatMoney: (n: number) => string,
  bases?: BasesDeReparto,
): string {
  const meses = mesesDelReparto(reparto);
  if (meses.length <= 1) return `Cuenta entero en ${mesEnPalabras(meses[0])}.`;
  const partes = partesDelReparto(total, reparto, bases);
  if (reparto.como === 'iguales' && !reparto.partes) {
    if (meses.length <= 6) return `${formatMoney(partes[0].importe)} al mes · ${meses[0].slice(0, 4) !== meses[meses.length - 1].slice(0, 4) ? meses.map(mesYAnio).join(', ') : `${listaDeMeses(meses, true)} ${meses[0].slice(0, 4)}`}`;
    return `Repartido de ${mesYAnio(meses[0])} a ${mesYAnio(meses[meses.length - 1])}: ${formatMoney(partes[0].importe)} al mes`;
  }
  const segun = reparto.partes ? 'Repartido' : `Repartido según ${reparto.como === 'ventas' ? 'ventas' : 'sueldos'}`;
  if (partes.length > 4) return `${segun} de ${mesYAnio(meses[0])} a ${mesYAnio(meses[meses.length - 1])}`;
  return `${segun}: ${partes.map((p) => `${nombreMesCorto(p.mes)} ${formatMoney(p.importe)}`).join(' · ')}`;
}

/* ------------------------------------------------------------------ */
/* Regla 8: el IVA que pasas a Hacienda no es gasto                    */
/* ------------------------------------------------------------------ */

const PAGO_DE_IVA = /\b((pago|declaracion|liquidacion|entero|saldo)\s+(a\s+pagar\s+)?(de(l)?\s+)?iva|iva\s+(a\s+pagar|del?\s+(trimestre|mes|periodo|bimestre)|trimestral|mensual|t[1-4])|modelo\s*(303|390)|iva\s+(de\s+)?(enero|febrero|marzo|abril|mayo|junio|julio|agosto|septiembre|octubre|noviembre|diciembre))\b/;
const RETENCIONES = /\b(retencion(es)?|isr\s+retenido|iva\s+retenido|modelo\s*(111|115|180|190))\b/;

/**
 * ¿Es un pago a Hacienda/SAT del IVA que cobraste o de retenciones? No es un
 * gasto: es dinero de tus clientes (o de tus empleados) que solo pasas.
 */
export function pagoQueNoEsGasto(concept: string): NoEsGasto | undefined {
  const texto = sinAcentos(concept ?? '');
  if (RETENCIONES.test(texto)) return 'retenciones';
  if (PAGO_DE_IVA.test(texto)) return 'iva';
  return undefined;
}

/** «El IVA que pagas a Hacienda no es un gasto: no lo cuento en tu resultado.» */
export function avisoNoEsGasto(tipo: NoEsGasto, pais?: string): string {
  const quien = pais === 'MX' ? 'al SAT' : pais === 'ES' || !pais ? 'a Hacienda' : 'a Hacienda';
  return tipo === 'iva'
    ? `El IVA que pagas ${quien} no es un gasto: es dinero de tus clientes que solo pasas. No lo cuento en tu resultado.`
    : `Las retenciones que pagas ${quien} no son un gasto: es dinero de otros que solo pasas. No las cuento en tu resultado.`;
}

/* ------------------------------------------------------------------ */
/* Regla 7: columna del Excel «¿Cubre varios meses?»                   */
/* ------------------------------------------------------------------ */

/**
 * Lee la columna opcional del Excel: «12 meses», «trimestre», «no». Vacía =
 * `undefined` (se aplica la regla 1 en «Antes de guardar»).
 */
export function leerCubreVariosMeses(valor: unknown, gasto: GastoParaRepartir, hoy?: string): RepartoGasto | undefined {
  const texto = sinAcentos(String(valor ?? '')).trim();
  if (!texto) return undefined;
  if (/^(no|0|1|1 mes|ninguno)$/.test(texto)) return repartoSoloDeEsteMes(gasto.date, hoy);
  if (/trimestr|bimestr|semestr/.test(texto)) return proponerReparto({ ...gasto, concept: texto }, hoy).reparto;
  const n = texto.match(/\b(\d{1,2})\b/);
  if (n && Number(n[1]) >= 2 && Number(n[1]) <= MAX_MESES_REPARTO) {
    return { ...proponerReparto(gasto, hoy).reparto, meses: Number(n[1]) };
  }
  if (/anual|ano|si/.test(texto)) return { meses: 12, desde: monthOf(gasto.date), como: 'iguales', ...(hoy ? { decididoEl: hoy } : {}) };
  return undefined;
}

/* ------------------------------------------------------------------ */
/* Guardar: `null` quita, ausente conserva                             */
/* ------------------------------------------------------------------ */

/**
 * El reparto y el «no es gasto» que se guardan al crear o editar un gasto.
 * Los formularios que no los conocen mandan el gasto entero SIN estos campos:
 * entonces se conserva lo que hubiera (como el justificante escaneado). Para
 * quitarlos a propósito se manda `null`.
 */
export function camposDeReparto(
  input: { reparto?: RepartoGasto | null; noEsGasto?: NoEsGasto | null },
  previo?: Pick<ExpenseEntry, 'reparto' | 'noEsGasto'>,
): { reparto: RepartoGasto | undefined; noEsGasto: NoEsGasto | undefined } {
  const reparto = input.reparto === null ? undefined : input.reparto ?? previo?.reparto ?? undefined;
  const noEsGasto = input.noEsGasto === null ? undefined : input.noEsGasto ?? previo?.noEsGasto ?? undefined;
  return { reparto, noEsGasto };
}
